import type { Point, Wall, FloorPlanElement, TextBox } from '../types';
import { CANVAS_SIZE } from '../constants/floorPlanConstants'; // Assuming CANVAS_SIZE is a general constant

export const constrainViewOffset = (
  offset: Point,
  scale: number,
  containerSize: number // Pass container size if it can vary, or use a constant
): Point => {
  // If CANVAS_SIZE refers to the total world size and containerSize is the viewport.
  // This logic might need adjustment based on how CANVAS_SIZE is used.
  // Assuming containerSize is the visible dimension of the canvas container on screen.
  // And CANVAS_SIZE * scale is the scaled dimensions of the logical canvas.

  // Let's assume CANVAS_SIZE is the logical size of the drawing area in world units.
  // The maxOffset calculation in the original code was:
  // const maxOffset = (scaledCanvasSize - containerSize) / (2 * newScale);
  // This seems to imply containerSize is the viewport and scaledCanvasSize is the content.
  // If scaledCanvasSize < containerSize, then maxOffset would be negative, which is fine for Math.max.
  
  const scaledContentWidth = CANVAS_SIZE * scale; // Example: logical canvas width scaled
  const scaledContentHeight = CANVAS_SIZE * scale; // Example: logical canvas height scaled

  // This interpretation is for centering content that might be smaller than the viewport
  // If the content is larger, this will prevent scrolling beyond its edges.

  const maxOffsetX = Math.max(0, (scaledContentWidth - containerSize) / (2 * scale));
  const maxOffsetY = Math.max(0, (scaledContentHeight - containerSize) / (2 * scale));


  // The original logic from FloorPlanCreator:
  // const maxOffset = (CANVAS_SIZE * newScale - containerSize) / (2 * newScale);
  // x: Math.max(-maxOffset, Math.min(maxOffset, offset.x)),
  // y: Math.max(-maxOffset, Math.min(maxOffset, offset.y))
  // This allows the canvas center to move within a certain range.
  // If CANVAS_SIZE is the world size, and containerSize is the viewport.

  // Let's stick to the original component's logic structure for maxOffset.
  // `containerSize` here would be the dimension of the viewport (e.g., canvasRef.current.clientWidth).
  // This needs to be passed or determined. For now, assuming it's a fixed 750 as in the original example.
  const effectiveContainerSize = 750; // This should ideally be dynamic (canvas width/height)

  const maxViewOffset = (CANVAS_SIZE * scale - effectiveContainerSize) / (2 * scale);

  return {
    x: Math.max(-maxViewOffset, Math.min(maxViewOffset, offset.x)),
    y: Math.max(-maxViewOffset, Math.min(maxViewOffset, offset.y))
  };
};

/**
 * Calculate bounding box for floor plan content including walls, elements, and text boxes
 */
export interface ContentBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export const calculateFloorPlanBounds = (
  walls: Wall[],
  elements: FloorPlanElement[] = [],
  textBoxes: TextBox[] = []
): ContentBounds | null => {
  let hasContent = false;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  // Include walls in bounds
  if (walls.length > 0) {
    walls.forEach(wall => {
      minX = Math.min(minX, wall.start.x, wall.end.x);
      minY = Math.min(minY, wall.start.y, wall.end.y);
      maxX = Math.max(maxX, wall.start.x, wall.end.x);
      maxY = Math.max(maxY, wall.start.y, wall.end.y);
      hasContent = true;
    });
  }

  // Include elements in bounds (exclude windows and doors as they should be within walls)
  if (elements.length > 0) {
    elements.forEach(element => {
      // Skip windows and doors as they should be contained within walls
      if ((element as any).type === 'window' || (element as any).type === 'door') {
        return;
      }
      
      // Handle different element types with position properties
      let elementBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
      
      if ('position' in element && element.position) {
        const pos = element.position as Point;
        const size = (element as any).size || 20; // Default size if not specified
        const width = (element as any).width || size;
        const height = (element as any).height || size;
        
        elementBounds = {
          minX: pos.x - width / 2,
          minY: pos.y - height / 2,
          maxX: pos.x + width / 2,
          maxY: pos.y + height / 2
        };
      } else if ('start' in element && 'end' in element) {
        // Handle line-like elements
        const start = (element as any).start as Point;
        const end = (element as any).end as Point;
        elementBounds = {
          minX: Math.min(start.x, end.x),
          minY: Math.min(start.y, end.y),
          maxX: Math.max(start.x, end.x),
          maxY: Math.max(start.y, end.y)
        };
      }
      
      if (elementBounds) {
        minX = Math.min(minX, elementBounds.minX);
        minY = Math.min(minY, elementBounds.minY);
        maxX = Math.max(maxX, elementBounds.maxX);
        maxY = Math.max(maxY, elementBounds.maxY);
        hasContent = true;
      }
    });
  }

  // Include text boxes in bounds
  if (textBoxes.length > 0) {
    textBoxes.forEach(textBox => {
      const boxMaxX = textBox.position.x + textBox.width;
      const boxMaxY = textBox.position.y + textBox.height;
      minX = Math.min(minX, textBox.position.x);
      minY = Math.min(minY, textBox.position.y);
      maxX = Math.max(maxX, boxMaxX);
      maxY = Math.max(maxY, boxMaxY);
      hasContent = true;
    });
  }

  if (!hasContent) {
    return null;
  }

  // Handle edge case where all content is at the same point
  const width = maxX === minX ? 1 : maxX - minX;
  const height = maxY === minY ? 1 : maxY - minY;
  const centerX = minX + width / 2;
  const centerY = minY + height / 2;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width,
    height,
    centerX,
    centerY
  };
};

/**
 * Calculate optimal view settings to center and fit floor plan content in the viewport
 */
export interface ViewSettings {
  scale: number;
  viewOffset: Point;
}

export const calculateCenteredView = (
  contentBounds: ContentBounds,
  containerWidth: number,
  containerHeight: number,
  paddingFactor: number = 0.9, // How much of the container to use (0.9 = 90% with 10% padding)
  maxScale: number = 5,
  minScale: number = 0.1
): ViewSettings => {
  // Calculate scale to fit content in the container
  const scaleX = (containerWidth * paddingFactor) / contentBounds.width;
  const scaleY = (containerHeight * paddingFactor) / contentBounds.height;
  const fitScale = Math.min(scaleX, scaleY);
  
  // Constrain scale within limits
  const scale = Math.max(minScale, Math.min(maxScale, fitScale));
  
  // Calculate view offset to center the content
  const viewOffsetX = contentBounds.centerX - (containerWidth / (2 * scale));
  const viewOffsetY = contentBounds.centerY - (containerHeight / (2 * scale));
  
  return {
    scale,
    viewOffset: { x: viewOffsetX, y: viewOffsetY }
  };
};

/**
 * Center floor plan content in the viewport with optimal scale
 * This is the main function to call when loading or switching floors
 */
export const centerFloorPlanContent = (
  walls: Wall[],
  elements: FloorPlanElement[] = [],
  textBoxes: TextBox[] = [],
  containerWidth: number = 750, // Default container size
  containerHeight: number = 600, // Default container size
  paddingFactor: number = 0.9
): ViewSettings | null => {
  const contentBounds = calculateFloorPlanBounds(walls, elements, textBoxes);
  
  if (!contentBounds) {
    // No content to center, return default centered view
    return {
      scale: 1,
      viewOffset: { x: -containerWidth / 2, y: -containerHeight / 2 }
    };
  }
  
  return calculateCenteredView(contentBounds, containerWidth, containerHeight, paddingFactor);
}; 